import {
  MeshBuilder,
  StandardMaterial,
  Color3,
  TransformNode,
  type Scene,
  type Mesh
} from '@babylonjs/core';
import type { Vector3, SlimeScale } from '@slimebound/core';

export interface SlimeMeshOptions {
  radius?: number;
  bodyColor?: Color3;
}

export class SlimeMesh {
  public readonly rootNode: TransformNode;
  public readonly bodyMesh: Mesh;
  public readonly leftEye: Mesh;
  public readonly rightEye: Mesh;
  private currentYaw: number = 0;

  constructor(scene: Scene, options: SlimeMeshOptions = {}) {
    const radius = options.radius ?? 0.9;
    this.rootNode = new TransformNode('slimeRoot', scene);

    // Core Gel Slime Body (Sphere with high tessellation for smooth curvature)
    this.bodyMesh = MeshBuilder.CreateSphere(
      'slimeBody',
      { diameter: radius * 2, segments: 32 },
      scene
    );
    this.bodyMesh.parent = this.rootNode;

    // Stylized Translucent Gel Material
    const gelMaterial = new StandardMaterial('slimeGelMat', scene);
    gelMaterial.diffuseColor = options.bodyColor ?? new Color3(0.12, 0.76, 0.95);
    gelMaterial.specularColor = new Color3(0.95, 0.98, 1.0);
    gelMaterial.specularPower = 128;
    gelMaterial.emissiveColor = new Color3(0.03, 0.18, 0.28);
    gelMaterial.alpha = 0.88;
    this.bodyMesh.material = gelMaterial;

    // Eye Material
    const eyeMat = new StandardMaterial('slimeEyeMat', scene);
    eyeMat.diffuseColor = new Color3(0.05, 0.08, 0.12);
    eyeMat.specularColor = new Color3(1, 1, 1);
    eyeMat.specularPower = 256;

    const eyePupilMat = new StandardMaterial('slimePupilMat', scene);
    eyePupilMat.emissiveColor = new Color3(1, 1, 1);

    // Left Eye
    this.leftEye = MeshBuilder.CreateSphere('slimeEyeL', { diameter: radius * 0.28, segments: 16 }, scene);
    this.leftEye.scaling.set(0.6, 1.2, 0.6);
    this.leftEye.position.set(-radius * 0.35, radius * 0.12, radius * 0.78);
    this.leftEye.parent = this.bodyMesh;
    this.leftEye.material = eyeMat;

    const leftPupil = MeshBuilder.CreateSphere('slimePupilL', { diameter: radius * 0.1, segments: 8 }, scene);
    leftPupil.position.set(-radius * 0.33, radius * 0.18, radius * 0.88);
    leftPupil.parent = this.bodyMesh;
    leftPupil.material = eyePupilMat;

    // Right Eye
    this.rightEye = MeshBuilder.CreateSphere('slimeEyeR', { diameter: radius * 0.28, segments: 16 }, scene);
    this.rightEye.scaling.set(0.6, 1.2, 0.6);
    this.rightEye.position.set(radius * 0.35, radius * 0.12, radius * 0.78);
    this.rightEye.parent = this.bodyMesh;
    this.rightEye.material = eyeMat;

    const rightPupil = MeshBuilder.CreateSphere('slimePupilR', { diameter: radius * 0.1, segments: 8 }, scene);
    rightPupil.position.set(radius * 0.33, radius * 0.18, radius * 0.88);
    rightPupil.parent = this.bodyMesh;
    rightPupil.material = eyePupilMat;
  }

  public update(position: Vector3, velocity: Vector3, scale: SlimeScale, dt: number): void {
    // Position sync
    this.rootNode.position.set(position.x, position.y, position.z);

    // Physical volume-preserving squish/stretch deformation
    this.bodyMesh.scaling.set(scale.scaleX, scale.scaleY, scale.scaleZ);

    // Smooth rotation facing direction of motion
    const horizontalSpeed = Math.hypot(velocity.x, velocity.z);
    if (horizontalSpeed > 0.2) {
      const targetYaw = Math.atan2(velocity.x, velocity.z);
      // Smooth angular interpolation
      let diff = targetYaw - this.currentYaw;
      while (diff > Math.PI) diff -= 2 * Math.PI;
      while (diff < -Math.PI) diff += 2 * Math.PI;
      this.currentYaw += diff * Math.min(1.0, 12.0 * dt);
      this.rootNode.rotation.y = this.currentYaw;
    }
  }

  public dispose(): void {
    this.bodyMesh.dispose();
    this.rootNode.dispose();
  }
}
